import React, { useState, useEffect } from 'react';
import './MarketResearchDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function MarketResearchDashboard() {
  const [selectedState, setSelectedState] = useState('SC');
  const [summary, setSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [stores, setStores] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningResearch, setRunningResearch] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);

  const US_STATES = {
    'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
    'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
    'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
    'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
    'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
    'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
    'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
    'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
    'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
    'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
  };

  useEffect(() => {
    loadStateData();
  }, [selectedState]);

  const loadStateData = async () => {
    setLoading(true);
    try {
      const [summaryRes, opportunitiesRes, storesRes, regionsRes] = await Promise.all([
        fetch(`${API_BASE}/api/market-research/summary/${selectedState}`),
        fetch(`${API_BASE}/api/market-research/opportunities/${selectedState}?limit=20`),
        fetch(`${API_BASE}/api/market-research/stores/${selectedState}`),
        fetch(`${API_BASE}/api/market-research/regions/${selectedState}`)
      ]);

      const summaryData = await summaryRes.json();
      const opportunitiesData = await opportunitiesRes.json();
      const storesData = await storesRes.json();
      const regionsData = await regionsRes.json();

      setSummary(summaryData);
      setOpportunities(opportunitiesData.opportunities || []);
      setStores(storesData.stores || []);
      setRegions(regionsData.regions || []);
    } catch (error) {
      console.error('Error loading state data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runFullCycle = async () => {
    if (!window.confirm(`Run full market research cycle for ${US_STATES[selectedState]}? This will take 30-60 minutes.`)) {
      return;
    }

    setRunningResearch(true);
    setProgress(null);
    try {
      const response = await fetch(`${API_BASE}/api/market-research/run-full-cycle/${selectedState}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      const result = await response.json();

      if (result.session_id) {
        setSessionId(result.session_id);
        // Start polling for progress
        pollProgress(result.session_id);
      }
    } catch (error) {
      console.error('Error starting research:', error);
      alert('Failed to start research cycle');
      setRunningResearch(false);
    }
  };

  const pollProgress = async (sid) => {
    const intervalId = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/api/market-research/status/${sid}`);

        if (!response.ok) {
          clearInterval(intervalId);
          setRunningResearch(false);
          return;
        }

        const status = await response.json();
        setProgress(status.progress);

        if (status.status === 'completed' || status.status === 'failed') {
          clearInterval(intervalId);
          setRunningResearch(false);
          setSessionId(null);

          if (status.status === 'completed') {
            loadStateData(); // Refresh data
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    }, 2000); // Poll every 2 seconds
  };

  const runGeographicStrategy = async () => {
    if (!window.confirm(`Run geographic strategy for ${US_STATES[selectedState]}?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/market-research/strategy/generate-regions/${selectedState}`, {
        method: 'POST'
      });

      const result = await response.json();
      alert(`Geographic strategy complete: ${result.regions_created} regions created`);
      loadStateData();
    } catch (error) {
      console.error('Error running geographic strategy:', error);
      alert('Failed to run geographic strategy');
    } finally {
      setLoading(false);
    }
  };

  const getOpportunityTypeColor = (type) => {
    const colors = {
      'new_prospect': '#4CAF50',
      'existing_upsell': '#2196F3',
      'retention_risk': '#FF9800',
      'competitor_threat': '#F44336'
    };
    return colors[type] || '#9E9E9E';
  };

  const getOpportunityTypeLabel = (type) => {
    const labels = {
      'new_prospect': 'New Prospect',
      'existing_upsell': 'Existing Upsell',
      'retention_risk': 'Retention Risk',
      'competitor_threat': 'Competitor Threat'
    };
    return labels[type] || type;
  };

  return (
    <div className="market-research-dashboard">
      <header className="dashboard-header">
        <h1>Market Research Intelligence</h1>
        <p className="subtitle">Multi-Agent Research Network</p>
      </header>

      <div className="state-selector">
        <label htmlFor="state-select">Select State:</label>
        <select
          id="state-select"
          value={selectedState}
          onChange={(e) => setSelectedState(e.target.value)}
          className="state-dropdown"
        >
          {Object.entries(US_STATES).map(([code, name]) => (
            <option key={code} value={code}>{name} ({code})</option>
          ))}
        </select>

        <div className="action-buttons">
          <button
            onClick={runGeographicStrategy}
            disabled={loading || runningResearch}
            className="btn btn-secondary"
          >
            Define Regions
          </button>
          <button
            onClick={runFullCycle}
            disabled={loading || runningResearch}
            className="btn btn-primary"
          >
            {runningResearch ? 'Running...' : 'Run Full Cycle'}
          </button>
          <button
            onClick={loadStateData}
            disabled={loading}
            className="btn btn-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      {runningResearch && progress && (
        <div className="progress-container">
          <h3>Research in Progress</h3>
          <div className="progress-info">
            <div className="progress-phase">
              <strong>Phase:</strong> {progress.phaseDescription}
            </div>
            <div className="progress-bar-container">
              <div className="progress-bar">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${(progress.currentStep / progress.totalSteps) * 100}%` }}
                />
              </div>
              <span className="progress-text">
                Step {progress.currentStep} of {progress.totalSteps}
              </span>
            </div>
            {progress.regionsTotal > 0 && (
              <div className="progress-regions">
                <strong>Regions:</strong> {progress.regionsCompleted} / {progress.regionsTotal}
                {progress.currentRegion && <span> (Researching: {progress.currentRegion})</span>}
              </div>
            )}
            {progress.storesFound > 0 && (
              <div className="progress-stats">
                <span><strong>Stores Found:</strong> {progress.storesFound}</span>
                {progress.opportunitiesFound > 0 && (
                  <span><strong>Opportunities:</strong> {progress.opportunitiesFound}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-value">{summary.total_regions || 0}</div>
            <div className="card-label">Regions</div>
          </div>
          <div className="summary-card">
            <div className="card-value">{summary.total_stores || 0}</div>
            <div className="card-label">Stores Researched</div>
          </div>
          <div className="summary-card">
            <div className="card-value">{summary.stores_with_candles || 0}</div>
            <div className="card-label">Carry Candles</div>
          </div>
          <div className="summary-card">
            <div className="card-value">{summary.total_opportunities || 0}</div>
            <div className="card-label">Opportunities</div>
          </div>
          <div className="summary-card highlight">
            <div className="card-value">
              ${summary.total_revenue_potential ? Math.round(summary.total_revenue_potential).toLocaleString() : '0'}
            </div>
            <div className="card-label">Revenue Potential</div>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {/* Regions Section */}
        {regions.length > 0 && (
          <div className="section">
            <h2>Regions</h2>
            <div className="regions-grid">
              {regions.map((region) => (
                <div key={region.region_id} className="region-card">
                  <h3>{region.region_name}</h3>
                  <div className="region-stats">
                    <div className="stat">
                      <span className="stat-label">Stores:</span>
                      <span className="stat-value">{region.total_stores || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Opportunities:</span>
                      <span className="stat-value">{region.total_opportunities || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Economic Tier:</span>
                      <span className={`tier tier-${region.economic_tier}`}>
                        {region.economic_tier}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Opportunities Section */}
        {opportunities.length > 0 && (
          <div className="section">
            <h2>Top Opportunities</h2>
            <div className="opportunities-table">
              <table>
                <thead>
                  <tr>
                    <th>Store</th>
                    <th>City</th>
                    <th>Type</th>
                    <th>Priority</th>
                    <th>Revenue Potential</th>
                    <th>Strength</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((opp) => (
                    <tr key={opp.id}>
                      <td><strong>{opp.store_name}</strong></td>
                      <td>{opp.city}</td>
                      <td>
                        <span
                          className="opportunity-type"
                          style={{ backgroundColor: getOpportunityTypeColor(opp.opportunity_type) }}
                        >
                          {getOpportunityTypeLabel(opp.opportunity_type)}
                        </span>
                      </td>
                      <td>
                        <div className="priority-score">
                          <div className="score-bar">
                            <div
                              className="score-fill"
                              style={{ width: `${opp.priority_score}%` }}
                            />
                          </div>
                          <span>{opp.priority_score}</span>
                        </div>
                      </td>
                      <td className="revenue-value">
                        ${opp.revenue_potential ? Math.round(opp.revenue_potential).toLocaleString() : '0'}
                      </td>
                      <td>{opp.strength_score || 'N/A'}</td>
                      <td>
                        <span className={`status-badge status-${opp.status}`}>
                          {opp.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* All Stores Section */}
        {stores.length > 0 && (
          <div className="section">
            <h2>All Stores ({stores.length})</h2>
            <div className="stores-grid">
              {stores.slice(0, 20).map((store) => (
                <div
                  key={store.id}
                  className="store-card clickable"
                  onClick={() => setSelectedStore(store)}
                >
                  <h4>{store.store_name}</h4>
                  <p className="store-location">{store.city}, {store.state}</p>
                  <div className="store-scores">
                    <div className="score">
                      <span className="score-label">Strength:</span>
                      <span className="score-value">{store.strength_score || 'N/A'}</span>
                    </div>
                    <div className="score">
                      <span className="score-label">Validity:</span>
                      <span className="score-value">{store.validity_score || 'N/A'}</span>
                    </div>
                  </div>
                  {store.carries_candles && (
                    <div className="badge">Carries Candles</div>
                  )}
                  <div className="store-type">{store.business_type || 'Unknown'}</div>
                  <div className="click-hint">Click for details →</div>
                </div>
              ))}
            </div>
            {stores.length > 20 && (
              <p className="showing-message">Showing 20 of {stores.length} stores</p>
            )}
          </div>
        )}

        {!loading && regions.length === 0 && (
          <div className="empty-state">
            <h3>No Research Data Available</h3>
            <p>Click "Define Regions" to start market research for {US_STATES[selectedState]}</p>
          </div>
        )}
      </div>

      {/* Store Detail Modal */}
      {selectedStore && (
        <div className="modal-overlay" onClick={() => setSelectedStore(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedStore(null)}>×</button>

            <div className="store-detail-header">
              <h2>{selectedStore.store_name}</h2>
              <p className="store-detail-location">
                {selectedStore.city}, {selectedStore.state}
              </p>
            </div>

            <div className="store-detail-body">
              {/* Scores */}
              <div className="detail-section">
                <h3>Scores</h3>
                <div className="score-bars">
                  <div className="score-bar-item">
                    <label>Strength Score</label>
                    <div className="score-bar-container">
                      <div className="score-bar-fill" style={{ width: `${selectedStore.strength_score || 0}%` }} />
                    </div>
                    <span>{selectedStore.strength_score || 'N/A'}</span>
                  </div>
                  <div className="score-bar-item">
                    <label>Validity Score</label>
                    <div className="score-bar-container">
                      <div className="score-bar-fill" style={{ width: `${selectedStore.validity_score || 0}%` }} />
                    </div>
                    <span>{selectedStore.validity_score || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="detail-section">
                <h3>Contact Information</h3>
                <div className="detail-grid">
                  {selectedStore.address && (
                    <div className="detail-item">
                      <span className="detail-label">Address:</span>
                      <span className="detail-value">{selectedStore.address}</span>
                    </div>
                  )}
                  {selectedStore.phone && (
                    <div className="detail-item">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">
                        <a href={`tel:${selectedStore.phone}`}>{selectedStore.phone}</a>
                      </span>
                    </div>
                  )}
                  {selectedStore.email && (
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">
                        <a href={`mailto:${selectedStore.email}`}>{selectedStore.email}</a>
                      </span>
                    </div>
                  )}
                  {selectedStore.website && (
                    <div className="detail-item">
                      <span className="detail-label">Website:</span>
                      <span className="detail-value">
                        <a href={selectedStore.website} target="_blank" rel="noopener noreferrer">
                          {selectedStore.website}
                        </a>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Media */}
              {(selectedStore.social_media || selectedStore.products_carried) && (
                <div className="detail-section">
                  <h3>Online Presence</h3>
                  <div className="detail-grid">
                    {selectedStore.social_media && (
                      <>
                        {selectedStore.social_media.instagram && (
                          <div className="detail-item">
                            <span className="detail-label">Instagram:</span>
                            <span className="detail-value">
                              <a
                                href={`https://instagram.com/${selectedStore.social_media.instagram}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                @{selectedStore.social_media.instagram}
                              </a>
                              {selectedStore.social_media.instagram_followers && (
                                <span className="follower-count">
                                  {' '}({selectedStore.social_media.instagram_followers.toLocaleString()} followers)
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        {selectedStore.social_media.facebook && (
                          <div className="detail-item">
                            <span className="detail-label">Facebook:</span>
                            <span className="detail-value">
                              <a
                                href={selectedStore.social_media.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Facebook Page
                              </a>
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Products & Details */}
              <div className="detail-section">
                <h3>Store Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Business Type:</span>
                    <span className="detail-value">{selectedStore.business_type || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Carries Candles:</span>
                    <span className="detail-value">
                      {selectedStore.carries_candles ? '✓ Yes' : '✗ No'}
                    </span>
                  </div>
                  {selectedStore.review_rating && (
                    <div className="detail-item">
                      <span className="detail-label">Review Rating:</span>
                      <span className="detail-value">
                        {selectedStore.review_rating} ⭐
                        {selectedStore.review_count && ` (${selectedStore.review_count} reviews)`}
                      </span>
                    </div>
                  )}
                  {selectedStore.products_carried && selectedStore.products_carried.price_range && (
                    <div className="detail-item">
                      <span className="detail-label">Price Range:</span>
                      <span className="detail-value">{selectedStore.products_carried.price_range}</span>
                    </div>
                  )}
                  {selectedStore.products_carried && selectedStore.products_carried.years_in_business && (
                    <div className="detail-item">
                      <span className="detail-label">Years in Business:</span>
                      <span className="detail-value">{selectedStore.products_carried.years_in_business}</span>
                    </div>
                  )}
                  {selectedStore.products_carried && selectedStore.products_carried.owner_name && (
                    <div className="detail-item">
                      <span className="detail-label">Owner:</span>
                      <span className="detail-value">{selectedStore.products_carried.owner_name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Candle Brands */}
              {selectedStore.products_carried && selectedStore.products_carried.candle_brands && selectedStore.products_carried.candle_brands.length > 0 && (
                <div className="detail-section">
                  <h3>Candle Brands Carried</h3>
                  <div className="brand-tags">
                    {selectedStore.products_carried.candle_brands.map((brand, idx) => (
                      <span key={idx} className="brand-tag">{brand}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedStore.products_carried && selectedStore.products_carried.notes && (
                <div className="detail-section">
                  <h3>Notes</h3>
                  <p className="store-notes">{selectedStore.products_carried.notes}</p>
                </div>
              )}

              {/* Research Info */}
              <div className="detail-section research-meta">
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Last Researched:</span>
                    <span className="detail-value">
                      {selectedStore.last_researched_at
                        ? new Date(selectedStore.last_researched_at).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`status-badge status-${selectedStore.status}`}>
                      {selectedStore.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketResearchDashboard;
